import { Alert, Box, Button, Divider, Group, Modal, SegmentedControl, Stack, Text, TextInput } from '@mantine/core';
import { useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { canAdjustOrderLinePrice, getMeasuredUnitLabel, parseLinePriceAdjustment, parseManualUnitPrice, resolveOrderLineCharge } from '@washday/utilities';
import { OrderContext } from '../../../context/OrderContext';
import type { IStore } from '../../../interfaces/Store';
import { formatMoneyString } from '../../../utils/strings';
import type { OrderProduct } from '../interfaces';
import type { OrderFormValues } from '../order.schema';
import { normalizeProductIdentity, normalizeSelectedOptionIdentity } from '../utils/lineIdentity';
import { PriceAdjustmentError, previewPriceAdjustment, type PriceAdjustment } from '../utils/priceAdjustment';

type ModalProps = { store: IStore | null | undefined; storeDiscounts?: unknown[] };

const PriceEditor = ({ product, store, storeDiscounts, onClose }: ModalProps & { product: OrderProduct; onClose: () => void }) => {
  const { t, i18n } = useTranslation(['NewOrder', 'CommonButtons']);
  const { control, getValues, setValue } = useFormContext<OrderFormValues>();
  const order = useWatch({ control }) as OrderFormValues;
  const initialCharge = resolveOrderLineCharge({ ...product, manualUnitPrice: null, extraAmount: 0 }, order.express);
  const [mode, setMode] = useState<PriceAdjustment['mode']>(product.manualUnitPrice != null || !product.extraAmount ? 'unit' : 'line');
  const [drafts, setDrafts] = useState(() => ({
    unit: (product.manualUnitPrice ?? initialCharge.selectedUnitPrice).toFixed(2),
    line: (product.manualUnitPrice != null ? 0 : product.extraAmount ?? 0).toFixed(2)
  }));
  const [applyError, setApplyError] = useState<string | null>(null);
  const submitting = useRef(false);
  const amount = (mode === 'unit' ? parseManualUnitPrice : parseLinePriceAdjustment)(drafts[mode]);
  const errorKey = (error: unknown) => error instanceof PriceAdjustmentError ? error.reason : 'previewUnavailable';
  const preview = useMemo(() => {
    if (amount === null) return { error: mode === 'unit' ? 'invalidUnitPrice' : 'invalidLineAdjustment', result: null };
    try {
      return { error: null, result: previewPriceAdjustment({ order, product, store, storeDiscounts,
        adjustment: mode === 'unit' ? { mode, unitPrice: amount } : { mode, extraAmount: amount } }) };
    } catch (error) { return { error: errorKey(error), result: null }; }
  }, [amount, mode, order, product, store, storeDiscounts]);
  const unit = getMeasuredUnitLabel(product.type, store?.measurementUnits);
  const unitLabel = unit || t('NewOrder:priceAdjustment.unit');
  const money = (value: number) => formatMoneyString(value, 2, '.', ',', store?.currencySymbol);
  const quantity = (value: number) => `${new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 3 }).format(value)}${unit ? ` ${unit}` : ''}`;
  const detailRow = (label: string, value: string | undefined, emphasis = false) => (
    <Group key={label} justify="space-between" align="baseline" wrap="nowrap" gap="md">
      <Text component="dt" fw={emphasis ? 700 : 400} size={emphasis ? 'lg' : 'sm'}>{label}</Text>
      <Text component="dd" m={0} fw={emphasis ? 700 : 500} size={emphasis ? 'lg' : 'sm'} ta="right" style={{ overflowWrap: 'anywhere' }}>{value}</Text>
    </Group>
  );
  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting.current || amount === null || !preview.result) return;
    submitting.current = true;
    try {
      // Read the latest form snapshot and repeat the exact preparation used by preview.
      const candidate = previewPriceAdjustment({ order: getValues(), product, store, storeDiscounts,
        adjustment: mode === 'unit' ? { mode, unitPrice: amount } : { mode, extraAmount: amount } });
      setValue('products', candidate.products);
      setValue('buyAndGetProducts', candidate.buyAndGetProducts);
      onClose();
    } catch (error) {
      submitting.current = false;
      setApplyError(errorKey(error));
    }
  };
  const result = preview.result;
  const charge = result?.catalogCharge ?? initialCharge;
  const error = preview.error ?? applyError;

  return <form onSubmit={submit}>
    <Stack gap="md">
      <Text fw={600} style={{ overflowWrap: 'anywhere' }}>{product.name}</Text>
      <Box component="dl" m={0}>{detailRow(t('NewOrder:priceAdjustment.catalogPrice'), money(charge.selectedUnitPrice))}</Box>
      <SegmentedControl fullWidth aria-label={t('NewOrder:priceAdjustment.mode')} value={mode}
        data={[{ value: 'unit', label: t('NewOrder:priceAdjustment.perUnit') }, { value: 'line', label: t('NewOrder:priceAdjustment.perLine') }]}
        onChange={value => { if (value === 'unit' || value === 'line') { setMode(value); setApplyError(null); } }} />
      <TextInput data-autofocus autoFocus value={drafts[mode]} inputMode={mode === 'line' ? 'text' : 'decimal'}
        label={mode === 'unit' ? t('NewOrder:priceAdjustment.unitPrice', { unit: unitLabel }) : t('NewOrder:priceAdjustment.lineAdjustment')}
        description={mode === 'line' ? t('NewOrder:priceAdjustment.lineHelp') : undefined}
        onChange={event => { setDrafts({ ...drafts, [mode]: event.currentTarget.value }); setApplyError(null); }}
        aria-invalid={Boolean(error)} aria-describedby={error ? 'price-adjustment-error' : undefined} />
      {error && <Alert color="red" role="alert" id="price-adjustment-error">{t(`NewOrder:priceAdjustment.${error}`)}</Alert>}
      <Stack component="dl" m={0} gap="xs" aria-live="polite" aria-atomic="true">
        {detailRow(t('NewOrder:priceAdjustment.quantity'), quantity(charge.actualQuantity))}
        {charge.minimumApplied && detailRow(t('NewOrder:priceAdjustment.minimumCharged'), quantity(charge.billableQuantity))}
        {result && <>
          {detailRow(t('NewOrder:priceAdjustment.subtotal'), money(result.lineTotals.subtotal))}
          {result.lineTotals.discount !== 0 && detailRow(t('NewOrder:priceAdjustment.discount'), `−${money(result.lineTotals.discount)}`)}
          {result.lineTotals.taxesTotal !== 0 && detailRow(t(store?.taxesType === 'included' ? 'NewOrder:priceAdjustment.taxesIncluded' : 'NewOrder:priceAdjustment.taxes'), money(result.lineTotals.taxesTotal))}
          <Divider />
          {detailRow(t('NewOrder:priceAdjustment.total'), money(result.lineTotals.total), true)}
          {((order.products?.length ?? 0) + (order.buyAndGetProducts?.length ?? 0) > 1 || Math.round(result.orderTotal * 100) !== Math.round(result.lineTotals.total * 100)) &&
            detailRow(t('NewOrder:priceAdjustment.orderTotal'), money(result.orderTotal))}
        </>}
      </Stack>
      <Group grow wrap="nowrap" pt="sm" pb="xs" style={{ position: 'sticky', bottom: 0, background: 'var(--mantine-color-body)' }}>
        <Button type="button" color="gray" variant="default" onClick={onClose}>{t('CommonButtons:cancel')}</Button>
        <Button type="submit" disabled={!result || Boolean(error)}>{t('NewOrder:priceAdjustment.apply')}</Button>
      </Group>
    </Stack>
  </form>;
};

const EditProductPriceModal = ({ store, storeDiscounts }: ModalProps) => {
  const { t } = useTranslation('NewOrder');
  const { setUpdatingProductPrice, updatingProductPrice, setEditProductPriceModal, editProductPriceModal } = useContext(OrderContext);
  const close = () => { setEditProductPriceModal(false); setUpdatingProductPrice(undefined); };
  const editableProduct = updatingProductPrice && canAdjustOrderLinePrice(updatingProductPrice) ? updatingProductPrice : null;
  return <Modal title={t('NewOrder:priceAdjustment.action')} opened={editProductPriceModal && Boolean(editableProduct)} onClose={close}
    size="sm" closeButtonProps={{ 'aria-label': t('NewOrder:priceAdjustment.close') }}>
    {editProductPriceModal && editableProduct && <PriceEditor key={editableProduct.lineId || editableProduct.orderProductLineID || `${normalizeProductIdentity(editableProduct)}:${normalizeSelectedOptionIdentity(editableProduct) ?? ''}`}
      product={editableProduct} store={store} storeDiscounts={storeDiscounts} onClose={close} />}
  </Modal>;
};

export default EditProductPriceModal;
